<template>
  <div class="global-smart-search">
    <button class="search-trigger" @click="openModal" :title="isMac ? '⌘K' : 'Ctrl+K'">
      <feather-icon icon="SearchIcon" size="18" />
      <span class="search-label">Buscar...</span>
      <kbd class="keyboard-hint">{{ isMac ? '⌘K' : 'Ctrl+K' }}</kbd>
    </button>

    <portal to="modal-portal">
      <transition name="modal-fade">
        <div v-if="isOpen" class="search-modal-overlay" @click.self="closeModal">
          <div class="search-modal" @keydown="handleKeydown">
          <div class="search-header">
            <div class="search-input-wrapper">
              <feather-icon icon="SearchIcon" size="18" class="search-icon" />
              <input
                ref="searchInput"
                type="text"
                class="search-input"
                :placeholder="uploadedImage ? 'Refinar busca por texto...' : 'Buscar por produto, código, categoria...'"
                v-model="query"
                @input="onInput"
                @paste="handlePaste"
                autocomplete="off"
              />
              <div v-if="isLoading || isImageSearching" class="loading-spinner">
                <b-spinner small variant="primary" />
              </div>
              <button v-if="query" class="clear-btn" @click="clearSearch" title="Limpar busca">
                <feather-icon icon="XIcon" size="16" />
              </button>
              <button class="image-upload-btn" @click="triggerImageUpload" title="Buscar por imagem">
                <feather-icon icon="CameraIcon" size="16" />
              </button>
              <input
                ref="imageInput"
                type="file"
                accept="image/*"
                class="hidden-file-input"
                @change="handleFileSelect"
              />
            </div>
            <button class="close-btn" @click="closeModal">
              <feather-icon icon="XIcon" size="20" />
            </button>
          </div>

          <div v-if="uploadedImage" class="image-preview-bar">
            <div class="image-preview-container">
              <img :src="uploadedImage" alt="Imagem para busca" class="preview-thumbnail" />
              <button class="remove-image-btn" @click="removeImage">
                <feather-icon icon="XIcon" size="12" />
              </button>
            </div>
            <div class="image-search-info">
              <span v-if="isImageSearching" class="image-status processing">
                <b-spinner small variant="primary" class="mr-50" />
                Analisando imagem...
              </span>
              <span v-else-if="imageSearchResult && imageSearchResult.exactMatch" class="image-status found">
                <feather-icon icon="CheckCircleIcon" size="14" class="mr-25" />
                Correspondência exata do catálogo
              </span>
              <span v-else-if="imageSearchResult && imageSearchResult.bestMatch" class="image-status found">
                <feather-icon icon="CheckCircleIcon" size="14" class="mr-25" />
                Candidato visual encontrado
              </span>
              <span v-else-if="imageSearchResult && !imageSearchResult.bestMatch" class="image-status not-found">
                <feather-icon icon="AlertCircleIcon" size="14" class="mr-25" />
                Nenhum produto similar encontrado
              </span>
            </div>
          </div>

          <div v-if="imageSearchResult && imageSearchResult.bestMatch && !hasResults" class="best-match-section">
            <div class="best-match-card" @click="selectImageProduct(imageSearchResult.bestMatch)">
              <div class="best-match-label">
                {{ imageSearchResult.exactMatch ? 'Correspondência exata do catálogo:' : 'Candidato visual mais provável:' }}
              </div>
              <div class="best-match-content">
                <div class="best-match-image" v-if="imageSearchResult.bestMatch.imagemUrl">
                  <img :src="getImageUrl(imageSearchResult.bestMatch.imagemUrl)" alt="" @error="handleImgError" />
                </div>
                <div class="best-match-image placeholder" v-else>
                  <feather-icon icon="BoxIcon" size="24" />
                </div>
                <div class="best-match-info">
                  <div class="best-match-title">{{ imageSearchResult.bestMatch.descricao }}</div>
                  <div class="best-match-subtitle">
                    {{ imageSearchResult.bestMatch.familiaComercialId }}
                    <span v-if="imageSearchResult.bestMatch.caracteristica"> · {{ truncate(imageSearchResult.bestMatch.caracteristica, 60) }}</span>
                  </div>
                </div>
                <div v-if="!ocultarEstoque" class="best-match-status">
                  <span class="status-badge status-success" v-if="imageSearchResult.bestMatch.situacao !== 'I'">Em estoque</span>
                  <span class="status-badge status-danger" v-else>Indisponível</span>
                </div>
              </div>
            </div>
          </div>

          <div class="search-body">
            <div class="categories-sidebar">
              <button
                class="category-item"
                :class="{ active: selectedCategory === 'all' }"
                @click="selectCategory('all')"
              >
                <feather-icon icon="GridIcon" size="16" />
                <span>Todas as categorias</span>
                <span class="category-count">{{ totalResults }}</span>
              </button>

              <div class="category-divider"></div>

              <button
                v-for="cat in categoriesWithResults"
                :key="cat.key"
                class="category-item"
                :class="{ active: selectedCategory === cat.key }"
                @click="selectCategory(cat.key)"
              >
                <feather-icon :icon="cat.icon" size="16" />
                <span>{{ cat.label }}</span>
                <span class="category-count">{{ cat.count }}</span>
              </button>
            </div>

            <div class="results-panel">
              <div v-if="(isLoading || isImageSearching) && !hasResults && !imageSearchResult" class="results-loading">
                <div class="skeleton-group" v-for="n in 3" :key="n">
                  <div class="skeleton-header"></div>
                  <div class="skeleton-item" v-for="m in 2" :key="m"></div>
                </div>
              </div>

              <div v-if="imageSearchResult && imageSearchResult.produtos && imageSearchResult.produtos.items.length > 0" class="image-results-section">
                <div class="result-group">
                  <div class="group-header">
                    <feather-icon icon="ImageIcon" size="14" />
                    <span>Produtos encontrados por imagem</span>
                    <span class="group-count">{{ imageSearchResult.produtos.items.length }}</span>
                  </div>
                  <div
                    v-for="(item, index) in imageSearchResult.produtos.items"
                    :key="`img-${index}`"
                    class="result-item"
                    :class="{ 'is-highlighted': highlightedGroup === 'image' && highlightedIndex === index }"
                    @click="selectImageProduct(item)"
                    @mouseenter="setHighlight('image', index)"
                  >
                    <div class="item-icon item-icon-image" v-if="item.imagemUrl">
                      <img :src="getImageUrl(item.imagemUrl)" alt="" @error="handleImgError" />
                    </div>
                    <div class="item-icon" v-else>
                      <feather-icon icon="BoxIcon" size="16" />
                    </div>
                    <div class="item-content">
                      <div class="item-title">{{ item.descricao || 'Produto sem descrição' }}</div>
                      <div class="item-subtitle">
                        {{ item.familiaComercialId || item.id }}
                        <span v-if="item.corNome"> · {{ item.corNome }}</span>
                        <span v-if="item.categoriaNome"> · {{ item.categoriaNome }}</span>
                      </div>
                    </div>
                    <div v-if="!ocultarEstoque" class="item-meta">
                      <span class="item-status status-success" v-if="item.situacao !== 'I'">Em estoque</span>
                      <span class="item-status status-danger" v-else>Indisponível</span>
                    </div>
                    <feather-icon icon="ChevronRightIcon" size="16" class="item-arrow" />
                  </div>
                </div>
              </div>

              <div v-if="!hasResults && !(imageSearchResult && imageSearchResult.produtos && imageSearchResult.produtos.items.length > 0) && query.length >= 2 && !isLoading && !isImageSearching" class="no-results">
                <feather-icon icon="SearchIcon" size="40" class="no-results-icon" />
                <h4>Nenhum resultado encontrado</h4>
                <p>Tente buscar por outro termo ou verifique a ortografia.</p>
              </div>

              <div v-if="query.length < 2 && !imageSearchResult && !hasResults" class="search-hint">
                <feather-icon icon="InfoIcon" size="24" class="hint-icon" />
                <p>Digite pelo menos 2 caracteres para iniciar a busca</p>
                <div class="hint-examples">
                  <span>Exemplos: Nome do cliente, número do pedido, CNPJ</span>
                </div>
                <div class="image-hint">
                  <feather-icon icon="CameraIcon" size="16" class="mr-25" />
                  <span>Ou cole/envie uma imagem de um produto para busca visual</span>
                </div>
              </div>

              <template v-if="hasResults">
                <div
                  v-for="group in filteredResults"
                  :key="group.key"
                  class="result-group"
                >
                  <div class="group-header">
                    <feather-icon :icon="group.icon" size="14" />
                    <span>{{ group.label }}</span>
                    <span class="group-count">{{ group.items.length }}</span>
                  </div>

                  <div
                    v-for="(item, index) in group.items.slice(0, expandedGroups[group.key] ? 20 : 5)"
                    :key="`${group.key}-${index}`"
                    class="result-item"
                    :class="{ 'is-highlighted': isHighlighted(group.key, index) }"
                    @click="selectItem(group.key, item)"
                    @mouseenter="setHighlight(group.key, index)"
                  >
                    <div class="item-icon" :class="{ 'item-icon-image': item.image }" v-if="item.image">
                      <img :src="getImageUrl(item.image)" alt="" @error="handleImgError" />
                    </div>
                    <div class="item-icon" v-else>
                      <feather-icon :icon="group.icon" size="16" />
                    </div>
                    <div class="item-content">
                      <div class="item-title" v-html="highlightMatch(item.title)"></div>
                      <div class="item-subtitle" v-html="highlightMatch(item.subtitle)"></div>
                    </div>
                    <div class="item-meta">
                      <span v-if="item.status" class="item-status" :class="getStatusClass(item.status)">
                        {{ item.status }}
                      </span>
                      <span v-if="item.value" class="item-value">{{ formatCurrency(item.value) }}</span>
                    </div>
                    <feather-icon icon="ChevronRightIcon" size="16" class="item-arrow" />
                  </div>

                  <button
                    v-if="group.items.length > 5 && !expandedGroups[group.key]"
                    class="see-more-btn"
                    @click="toggleExpand(group.key)"
                  >
                    Ver mais {{ group.items.length - 5 }} resultados
                  </button>
                  <button
                    v-else-if="expandedGroups[group.key]"
                    class="see-more-btn"
                    @click="toggleExpand(group.key)"
                  >
                    Ver menos
                  </button>
                </div>
              </template>
            </div>
          </div>

          <div class="search-footer">
            <div class="footer-hints">
              <span class="hint">
                <kbd>↑</kbd><kbd>↓</kbd> navegar
              </span>
              <span class="hint">
                <kbd>Enter</kbd> selecionar
              </span>
              <span class="hint">
                <kbd>Esc</kbd> fechar
              </span>
              <span class="hint">
                <feather-icon icon="CameraIcon" size="10" /> Ctrl+V imagem
              </span>
            </div>
          </div>
          </div>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
import axios from '@/libs/axios';
import { Portal } from 'portal-vue';
import { resolveImagemUrl, IMAGEM_PADRAO } from '@/utils/imagemUrl';
import { PERFIS_CLIENTE, isPerfilClienteAtivo } from '@/libs/perfilCliente';
import { ocultarEstoqueCertificacao, tipoPedidoSelecionadoParaEstoque } from '@/utils/estoqueVisibilidade';
import { extractImgSrcFromClipboardHtml, findPastedImageSourceOnPage } from '@/libs/catalogImageMatch';

export default {
  name: 'GlobalSmartSearch',
  components: {
    Portal,
  },
  data() {
    return {
      isOpen: false,
      query: '',
      isLoading: false,
      isImageSearching: false,
      uploadedImage: null,
      uploadedImageFile: null,
      imageSearchResult: null,
      debounceTimer: null,
      highlightedGroup: null,
      highlightedIndex: 0,
      selectedCategory: 'all',
      expandedGroups: {},
      results: {
        clientes: { items: [], total: 0 },
        pedidos: { items: [], total: 0 },
        rascunhos: { items: [], total: 0 },
        produtos: { items: [], total: 0 },
        representantes: { items: [], total: 0 },
        atividades_crm: { items: [], total: 0 },
        oportunidades_crm: { items: [], total: 0 },
      },
    };
  },
  computed: {
    ocultarEstoque() {
      return isPerfilClienteAtivo() || ocultarEstoqueCertificacao(tipoPedidoSelecionadoParaEstoque(this.$store));
    },
    isMac() {
      return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    },
    userProfileName() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
        return perfilLogado?.perfil_acesso_nome || null;
      } catch (error) {
        return null;
      }
    },
    restrictedCategories() {
      if (PERFIS_CLIENTE.includes(this.userProfileName)) return ['clientes', 'representantes'];
      if (this.userProfileName === 'Representante') return ['representantes'];
      return [];
    },
    categories() {
      const perfilNome = this.userProfileName;
      const isInternal = perfilNome && !PERFIS_CLIENTE.includes(perfilNome);
      const baseCategories = [
        { key: 'clientes', label: 'Clientes', icon: 'UsersIcon' },
        { key: 'pedidos', label: 'Pedidos', icon: 'PackageIcon' },
        { key: 'rascunhos', label: 'Rascunhos', icon: 'FileTextIcon' },
        { key: 'produtos', label: 'Produtos', icon: 'BoxIcon' },
        { key: 'representantes', label: 'Representantes', icon: 'UserCheckIcon' },
        ...(isInternal ? [
          { key: 'atividades_crm', label: 'Atividades CRM', icon: 'CalendarIcon' },
          { key: 'oportunidades_crm', label: 'Oportunidades CRM', icon: 'TargetIcon' },
        ] : []),
      ];

      if (this.restrictedCategories.length === 0) return baseCategories;

      return baseCategories.filter((cat) => !this.restrictedCategories.includes(cat.key));
    },
    categoriesWithResults() {
      return this.categories
        .map((cat) => ({
          ...cat,
          count: this.results[cat.key]?.items?.length || 0,
        }))
        .filter((cat) => cat.count > 0);
    },
    totalResults() {
      return this.categoriesWithResults.reduce((sum, cat) => sum + cat.count, 0);
    },
    hasResults() {
      return this.totalResults > 0;
    },
    filteredResults() {
      const groups = [];

      for (const cat of this.categories) {
        if (this.selectedCategory !== 'all' && this.selectedCategory !== cat.key) continue;

        const items = this.results[cat.key]?.items || [];
        if (items.length === 0) continue;

        groups.push({
          key: cat.key,
          label: cat.label,
          icon: cat.icon,
          items: this.transformItems(cat.key, items),
        });
      }

      return groups;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    handleGlobalKeydown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        this.openModal();
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.closeModal();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.navigateDown();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateUp();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.selectCurrent();
      }
    },
    openModal() {
      this.isOpen = true;
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    closeModal() {
      this.isOpen = false;
      this.query = '';
      this.selectedCategory = 'all';
      this.expandedGroups = {};
      this.uploadedImage = null;
      this.uploadedImageFile = null;
      this.imageSearchResult = null;
      this.resetResults();
    },
    resetResults() {
      this.results = {
        clientes: { items: [], total: 0 },
        pedidos: { items: [], total: 0 },
        rascunhos: { items: [], total: 0 },
        produtos: { items: [], total: 0 },
        representantes: { items: [], total: 0 },
        atividades_crm: { items: [], total: 0 },
        oportunidades_crm: { items: [], total: 0 },
      };
    },
    clearSearch() {
      this.query = '';
      this.resetResults();
      this.$refs.searchInput?.focus();
    },
    onInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);

      if (this.query.length < 2) {
        this.resetResults();
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch();
      }, 350);
    },
    async performSearch() {
      if (this.query.length < 2) return;

      this.isLoading = true;

      try {
        const response = await axios.get('/search/global', {
          params: { q: this.query },
        });

        if (response.data?.status === 'success') {
          this.results = response.data.data;
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        this.isLoading = false;
      }
    },
    handlePaste(e) {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            // Identificação determinística: extrai o src do <img> copiado
            // (text/html do clipboard) antes de qualquer análise visual.
            const srcHint = extractImgSrcFromClipboardHtml(e.clipboardData);
            this.processImageFile(file, { sourceHint: srcHint, fromPaste: true });
          }
          return;
        }
      }
    },
    triggerImageUpload() {
      this.$refs.imageInput?.click();
    },
    handleFileSelect(e) {
      const file = e.target.files?.[0];
      if (file && file.type.startsWith('image/')) {
        this.processImageFile(file);
      }
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = '';
      }
    },
    processImageFile(file, { sourceHint = null, fromPaste = false } = {}) {
      this.uploadedImageFile = file;
      const reader = new FileReader();
      reader.onload = async (e) => {
        this.uploadedImage = e.target.result;
        let src = sourceHint;
        if (!src && fromPaste) {
          // Fallback: compara pixels com as imagens já carregadas na página.
          try { src = await findPastedImageSourceOnPage(e.target.result); } catch (err) { src = null; }
        }
        this.performImageSearch(file, src);
      };
      reader.readAsDataURL(file);
    },
    async performImageSearch(file, sourceImageUrl = null) {
      this.isImageSearching = true;
      this.imageSearchResult = null;

      try {
        const formData = new FormData();
        formData.append('image', file);
        if (sourceImageUrl) formData.append('sourceImageUrl', sourceImageUrl);

        const response = await axios.post('/search/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data?.status === 'success') {
          this.imageSearchResult = response.data.data;

          if (this.imageSearchResult.terms && this.imageSearchResult.terms.length > 0 && !this.query) {
            this.query = this.imageSearchResult.terms.slice(0, 3).join(' / ');
          } else if (this.imageSearchResult.extractedColors && this.imageSearchResult.extractedColors.length > 0 && !this.query) {
            const colorNames = this.imageSearchResult.extractedColors
              .slice(0, 2)
              .map(c => c.name)
              .filter(n => n !== 'cinza escuro');
            if (colorNames.length > 0) {
              this.query = colorNames.join(' / ');
            }
          }
        }
      } catch (error) {
        console.error('Image search error:', error);
        this.imageSearchResult = {
          extractedText: '',
          terms: [],
          bestMatch: null,
          produtos: { items: [], total: 0 },
        };
      } finally {
        this.isImageSearching = false;
      }
    },
    removeImage() {
      this.uploadedImage = null;
      this.uploadedImageFile = null;
      this.imageSearchResult = null;
      this.$refs.searchInput?.focus();
    },
    getImageUrl(path) {
      if (!path) return IMAGEM_PADRAO;
      return path;
    },
    handleImgError(e) {
      if (!e.target.dataset.fallback) {
        e.target.dataset.fallback = '1';
        e.target.src = IMAGEM_PADRAO;
      }
    },
    truncate(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    selectImageProduct(item) {
      this.closeModal();
      // Correspondência exata do catálogo → navega para o catálogo da marca
      // com categoria e referência pré-preenchidas (não para lista genérica).
      if (item && item.exactCatalogMatch && item.codMarca) {
        const query = {};
        if (item.categoriaCodigo != null) query.categoria = String(item.categoriaCodigo);
        if (item.familiaComercialId) query.referencia = String(item.familiaComercialId);
        this.$router.push({
          name: 'produtos-catalogo-marca',
          params: { codMarca: String(item.codMarca) },
          query,
        }).catch(() => {});
        return;
      }
      // Mesma navegação da busca por nome (case 'produtos'): vai para a home
      // com o filtro do produto, em vez de abrir a tabela de itens.
      const query = {};
      const codigo = item && (item.codigoErp || item.id || item.item_id || item.itemId);
      if (codigo) {
        query.codigo = String(codigo);
      } else if (item && item.descricao) {
        query.nome = String(item.descricao);
      }
      this.$router.push({
        name: 'home',
        query,
      }).catch(() => {});
    },
    transformItems(category, items) {
      return items.map((item) => {
        switch (category) {
          case 'clientes':
            return {
              id: item.id,
              title: item.razaoSocial || item.nomeFantasia || 'Cliente sem nome',
              subtitle: `Código: ${item.codigoErp || item.id} | CNPJ: ${item.cnpjCpf || '-'}`,
              raw: item,
            };
          case 'pedidos':
            return {
              id: item.id,
              title: `Pedido #${item.prePedido}${item.pedidoErp ? ` (ERP: ${item.pedidoErp})` : ''}`,
              subtitle: item.clienteRazaoSocial || 'Cliente não informado',
              status: item.status,
              value: item.valorTotal,
              raw: item,
            };
          case 'rascunhos':
            return {
              id: item.id,
              title: `Rascunho #${item.prePedido}`,
              subtitle: `${item.clienteRazaoSocial || 'Cliente não informado'} - ${item.usuarioNome || ''}`,
              value: item.valorTotal,
              raw: item,
            };
          case 'produtos':
            return {
              id: item.id,
              title: item.descricao || 'Produto sem descrição',
              subtitle: `Código: ${item.codigoErp || item.familiaComercialId || item.id}${item.marca ? ` | Marca: ${item.marca}` : ''}`,
              image: item.imagemUrl || null,
              raw: item,
            };
          case 'representantes':
            return {
              id: item.id,
              title: item.nome || 'Representante sem nome',
              subtitle: `Código: ${item.codigoErp || item.id}${item.email ? ` | ${item.email}` : ''}`,
              raw: item,
            };
          case 'atividades_crm':
            return {
              id: item.id,
              title: `[${item.tipo}] ${item.titulo}`,
              subtitle: `${item.cliente_nome || 'Sem cliente'} | ${item.status} | ${item.data_inicio ? new Date(item.data_inicio).toLocaleDateString('pt-BR') : ''}`,
              status: item.status,
              raw: item,
            };
          case 'oportunidades_crm':
            return {
              id: item.id,
              title: item.titulo,
              subtitle: `${item.cliente_nome || 'Sem cliente'} | ${item.status} | R$ ${parseFloat(item.valor_estimado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
              status: item.status,
              raw: item,
            };
          default:
            return {
              id: item.id, title: 'Item', subtitle: '', raw: item,
            };
        }
      });
    },
    selectCategory(category) {
      this.selectedCategory = category;
      this.highlightedGroup = null;
      this.highlightedIndex = 0;
    },
    toggleExpand(groupKey) {
      this.$set(this.expandedGroups, groupKey, !this.expandedGroups[groupKey]);
    },
    highlightMatch(text) {
      if (!text || !this.query || this.query.length < 2) return text;

      const escapedQuery = this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return String(text).replace(regex, '<mark>$1</mark>');
    },
    formatCurrency(value) {
      const num = parseFloat(value) || 0;
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    getStatusClass(status) {
      if (!status) return '';
      const s = String(status).toLowerCase();
      if (s.includes('aprovado') || s.includes('faturado')) return 'status-success';
      if (s.includes('rascunho') || s.includes('pendente')) return 'status-warning';
      if (s.includes('cancelado') || s.includes('rejeitado')) return 'status-danger';
      return 'status-default';
    },
    navigateDown() {
      const groups = this.filteredResults;
      if (groups.length === 0) return;

      if (this.highlightedGroup === null) {
        this.highlightedGroup = groups[0].key;
        this.highlightedIndex = 0;
        return;
      }

      const currentGroupIndex = groups.findIndex((g) => g.key === this.highlightedGroup);
      const currentGroup = groups[currentGroupIndex];
      const maxIndex = Math.min(currentGroup.items.length - 1, this.expandedGroups[currentGroup.key] ? 19 : 4);

      if (this.highlightedIndex < maxIndex) {
        this.highlightedIndex++;
      } else if (currentGroupIndex < groups.length - 1) {
        this.highlightedGroup = groups[currentGroupIndex + 1].key;
        this.highlightedIndex = 0;
      }
    },
    navigateUp() {
      const groups = this.filteredResults;
      if (groups.length === 0 || this.highlightedGroup === null) return;

      const currentGroupIndex = groups.findIndex((g) => g.key === this.highlightedGroup);

      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      } else if (currentGroupIndex > 0) {
        const prevGroup = groups[currentGroupIndex - 1];
        this.highlightedGroup = prevGroup.key;
        this.highlightedIndex = Math.min(prevGroup.items.length - 1, this.expandedGroups[prevGroup.key] ? 19 : 4);
      }
    },
    selectCurrent() {
      if (this.highlightedGroup === null) return;
      const group = this.filteredResults.find((g) => g.key === this.highlightedGroup);
      if (group && group.items[this.highlightedIndex]) {
        this.selectItem(this.highlightedGroup, group.items[this.highlightedIndex]);
      }
    },
    isHighlighted(groupKey, index) {
      return this.highlightedGroup === groupKey && this.highlightedIndex === index;
    },
    setHighlight(groupKey, index) {
      this.highlightedGroup = groupKey;
      this.highlightedIndex = index;
    },
    selectItem(category, item) {
      const searchQuery = this.query;
      this.closeModal();

      const query = searchQuery ? { q: searchQuery } : undefined;

      switch (category) {
        case 'clientes': {
          const filtro = (item.raw && (item.raw.cnpjCpf || item.raw.codigoErp))
            || item.title;
          this.$router.push({
            name: 'apps-cliente-list',
            query: { q: filtro },
          }).catch(() => {});
          break;
        }
        case 'pedidos':
          this.$router.push({
            name: 'pedidos-vendas-detalhes',
            params: { pedidoid: item.id, clienteid: item.raw.clienteId },
          }).catch(() => {});
          break;
        case 'rascunhos':
          this.$router.push({
            name: 'pedidos-vendas-detalhes',
            params: { pedidoid: item.id, clienteid: item.raw.clienteId },
          }).catch(() => {});
          break;
        case 'produtos': {
          const query = {};
          const codigo = item.raw && (item.raw.codigoErp || item.raw.item_id || item.raw.itemId);
          if (codigo) {
            query.codigo = String(codigo);
          } else if (item.title) {
            query.nome = String(item.title);
          }
          this.$router.push({
            name: 'home',
            query,
          }).catch(() => {});
          break;
        }
        case 'representantes':
          this.$router.push({
            name: 'apps-representante-list',
            params: { id: item.id },
            query,
          }).catch(() => {});
          break;
        case 'atividades_crm':
          this.$router.push({ name: 'crm-agenda' }).catch(() => {});
          break;
        case 'oportunidades_crm':
          this.$router.push({ name: 'crm-oportunidades' }).catch(() => {});
          break;
      }
    },
  },
};
</script>

<style scoped>
.global-smart-search {
  display: flex;
  align-items: center;
}

.search-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  height: 36px;
  background: var(--sf-header-control-bg, #ffffff);
  border: 1px solid var(--sf-header-control-border, #e2e8f0);
  border-radius: 8px;
  color: var(--sf-header-control-icon, #64748b);
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 200px;
}

.search-trigger:hover {
  background: var(--sf-header-control-hover-bg, #f8fafc);
  border-color: var(--sf-accent, #6366f1);
}

.search-label {
  flex: 1;
  text-align: left;
  font-size: 0.8125rem;
  color: var(--sf-header-control-icon, #94a3b8);
}

.keyboard-hint {
  font-size: 0.625rem;
  font-weight: 500;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: var(--sf-header-control-icon, #94a3b8);
  background: var(--sf-surface-bg, #f1f5f9);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  border: 1px solid var(--sf-header-control-border, #e2e8f0);
}

.search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 99999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 10vh 1rem;
}

.search-modal {
  width: 100%;
  max-width: 800px;
  max-height: 70vh;
  background: var(--sf-modal-bg, #ffffff);
  border: 1px solid var(--sf-border-color);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.search-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid var(--sf-border-color, #e5e7eb);
}

.search-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--sf-surface-secondary, #f8fafc);
  border: 1px solid var(--sf-border-color, #e5e7eb);
  border-radius: 8px;
}

.search-input-wrapper:focus-within {
  border-color: var(--sf-accent, #6366f1);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-icon {
  color: var(--sf-text-muted, #94a3b8);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 1rem;
  color: var(--sf-text-primary, #1e293b);
  outline: none;
}

.search-input::placeholder {
  color: var(--sf-text-muted, #94a3b8);
}

.loading-spinner {
  display: flex;
  align-items: center;
}

.clear-btn,
.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: none;
  border: none;
  color: var(--sf-text-muted, #94a3b8);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.clear-btn:hover,
.close-btn:hover {
  color: var(--sf-text-primary, #1e293b);
  background: var(--sf-surface-secondary, #f1f5f9);
}

.image-upload-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  background: none;
  border: 1px solid var(--sf-border-color, #e2e8f0);
  color: var(--sf-text-muted, #64748b);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.image-upload-btn:hover {
  color: var(--sf-accent, #6366f1);
  border-color: var(--sf-accent, #6366f1);
  background: rgba(99, 102, 241, 0.05);
}

.hidden-file-input {
  display: none;
}

.image-preview-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--sf-surface-secondary, #f8fafc);
  border-bottom: 1px solid var(--sf-border-color, #e5e7eb);
}

.image-preview-container {
  position: relative;
  flex-shrink: 0;
}

.preview-thumbnail {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid var(--sf-border-color, #e2e8f0);
}

.remove-image-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: #ffffff;
  border: 2px solid var(--sf-modal-bg, #ffffff);
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;
}

.remove-image-btn:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.image-search-info {
  flex: 1;
}

.image-status {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 500;
}

.image-status.processing {
  color: var(--sf-accent, #6366f1);
}

.image-status.found {
  color: #059669;
}

.image-status.not-found {
  color: #d97706;
}

.best-match-section {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--sf-border-color, #e5e7eb);
}

.best-match-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.05));
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.best-match-card:hover {
  border-color: var(--sf-accent, #6366f1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
}

.best-match-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--sf-accent, #6366f1);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
}

.best-match-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.best-match-image {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--sf-surface-secondary, #f8fafc);
  display: flex;
  align-items: center;
  justify-content: center;
}

.best-match-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.best-match-image.placeholder {
  color: var(--sf-text-muted, #94a3b8);
}

.best-match-info {
  flex: 1;
  min-width: 0;
}

.best-match-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--sf-text-primary, #1e293b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.best-match-subtitle {
  font-size: 0.75rem;
  color: var(--sf-text-muted, #64748b);
  margin-top: 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.best-match-status {
  flex-shrink: 0;
}

.status-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  white-space: nowrap;
}

.status-badge.status-success {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-badge.status-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.image-results-section {
  padding: 0;
}

.item-icon-image {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--sf-surface-tertiary, #f1f5f9);
  padding: 0;
}

.item-icon-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: var(--sf-text-muted, #94a3b8);
  padding: 0.5rem 1rem;
  background: var(--sf-surface-secondary, #f8fafc);
  border-radius: 6px;
  border: 1px dashed var(--sf-border-color, #e2e8f0);
}

.search-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.categories-sidebar {
  width: 200px;
  flex-shrink: 0;
  padding: 0.5rem;
  border-right: 1px solid var(--sf-border-color, #e5e7eb);
  overflow-y: auto;
  background: var(--sf-surface-secondary, #f8fafc);
}

.category-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: var(--sf-text-secondary, #334155);
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.category-item:hover {
  background: var(--sf-surface-tertiary, #e2e8f0);
}

.category-item.active {
  background: var(--sf-accent, #6366f1);
  color: #ffffff;
}

.category-item.active .category-count {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.category-count {
  margin-left: auto;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  background: var(--sf-border-light, #e2e8f0);
  border-radius: 4px;
  color: var(--sf-text-muted, #64748b);
}

.category-divider {
  height: 1px;
  background: var(--sf-border-color, #e2e8f0);
  margin: 0.5rem 0;
}

.results-panel {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  background: var(--sf-modal-bg, #ffffff);
}

.results-loading {
  padding: 1rem;
}

.skeleton-group {
  margin-bottom: 1.5rem;
}

.skeleton-header {
  height: 14px;
  width: 100px;
  background: var(--sf-border-light, #e2e8f0);
  border-radius: 4px;
  margin-bottom: 0.75rem;
  animation: pulse 1.5s infinite;
}

.skeleton-item {
  height: 48px;
  background: var(--sf-surface-secondary, #f8fafc);
  border-radius: 6px;
  margin-bottom: 0.5rem;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.no-results,
.search-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: var(--sf-text-muted, #94a3b8);
}

.no-results-icon,
.hint-icon {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-results h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--sf-text-primary, #1e293b);
  margin-bottom: 0.25rem;
}

.no-results p,
.search-hint p {
  font-size: 0.875rem;
  margin: 0;
}

.hint-examples {
  margin-top: 1rem;
  font-size: 0.75rem;
  color: var(--sf-text-muted, #94a3b8);
}

.result-group {
  margin-bottom: 1.25rem;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--sf-text-muted, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.375rem;
}

.group-count {
  margin-left: auto;
  font-size: 0.625rem;
  padding: 0.0625rem 0.25rem;
  background: var(--sf-border-light, #e2e8f0);
  border-radius: 3px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.1s ease;
}

.result-item:hover,
.result-item.is-highlighted {
  background: var(--sf-surface-secondary, #f1f5f9);
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--sf-surface-tertiary, #f1f5f9);
  border-radius: 6px;
  color: var(--sf-text-muted, #64748b);
  flex-shrink: 0;
}

.result-item.is-highlighted .item-icon,
.result-item:hover .item-icon {
  background: var(--sf-accent, #6366f1);
  color: #ffffff;
}

.result-item.is-highlighted .item-icon-image,
.result-item:hover .item-icon-image {
  background: var(--sf-surface-tertiary, #f1f5f9);
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--sf-text-primary, #1e293b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-title :deep(mark) {
  background: var(--sf-accent-bg, rgba(99, 102, 241, 0.2));
  color: var(--sf-accent, #6366f1);
  padding: 0 0.125rem;
  border-radius: 2px;
}

.item-subtitle {
  font-size: 0.75rem;
  color: var(--sf-text-muted, #64748b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-subtitle :deep(mark) {
  background: var(--sf-accent-bg, rgba(99, 102, 241, 0.15));
  color: var(--sf-accent, #6366f1);
  padding: 0 0.125rem;
  border-radius: 2px;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.item-status {
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.1875rem 0.375rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.status-success {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-warning {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.status-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

:global(body.dark-layout) .status-success {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}
:global(body.dark-layout) .status-warning {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}
:global(body.dark-layout) .status-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

:global(body.dark-layout) .best-match-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.1));
  border-color: rgba(99, 102, 241, 0.3);
}

:global(body.dark-layout) .status-badge.status-success {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

:global(body.dark-layout) .status-badge.status-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

:global(body.dark-layout) .image-preview-bar {
  background: var(--sf-surface-secondary, #1e293b);
}

:global(body.dark-layout) .image-hint {
  background: var(--sf-surface-secondary, #1e293b);
  border-color: var(--sf-border-color, #334155);
}

.status-default {
  background: var(--sf-border-light, #e2e8f0);
  color: var(--sf-text-muted, #64748b);
}

.item-value {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--sf-text-secondary, #334155);
}

.item-arrow {
  color: var(--sf-text-muted, #94a3b8);
  opacity: 0;
  transition: opacity 0.1s ease;
}

.result-item:hover .item-arrow,
.result-item.is-highlighted .item-arrow {
  opacity: 1;
}

.see-more-btn {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--sf-accent, #6366f1);
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.1s ease;
}

.see-more-btn:hover {
  background: var(--sf-surface-secondary, #f8fafc);
}

.search-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--sf-border-color, #e5e7eb);
  background: var(--sf-surface-secondary, #f8fafc);
}

.footer-hints {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
}

.hint {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  color: var(--sf-text-muted, #64748b);
}

.hint kbd {
  font-size: 0.625rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  padding: 0.125rem 0.25rem;
  background: var(--sf-modal-bg, #ffffff);
  border: 1px solid var(--sf-border-color, #e2e8f0);
  border-radius: 3px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .search-modal,
.modal-fade-leave-to .search-modal {
  transform: scale(0.95) translateY(-20px);
}

@media (max-width: 768px) {
  .search-trigger {
    min-width: auto;
    padding: 0.375rem;
  }

  .search-label,
  .keyboard-hint {
    display: none;
  }

  .search-modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .search-modal {
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
  }

  .search-body {
    flex-direction: column;
  }

  .categories-sidebar {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    display: flex;
    gap: 0.25rem;
    padding: 0.5rem;
    border-right: none;
    border-bottom: 1px solid var(--sf-border-color, #e5e7eb);
  }

  .category-item {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .category-divider {
    display: none;
  }
}
</style>
